import * as React from "react";
import {
  Box,
  CircularProgress,
  Link,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useQuery } from "react-query";
import {
  ApplicationEventTemplateVariable,
  ApplicationEventTemplateVariables,
  ApplicationEventType,
  ErrorResponse,
  UtilsService
} from "gen/clients";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";

interface Props {
  eventType: ApplicationEventType | "" | undefined;
}

const TemplateVariablesPopover: React.FC<Props> = ({ eventType }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const { data, isLoading, error } = useQuery<ApplicationEventTemplateVariables, ErrorResponse>(
    ["getApplicationEventTemplateVariables", eventType],
    () => UtilsService.getApplicationEventTemplateVariables({ eventType: eventType as ApplicationEventType }),
    { enabled: !!eventType, refetchOnWindowFocus: false }
  );

  const handleOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  if (!eventType) {
    return null;
  }

  const variables = data?.variables || [];

  return (
    <Box sx={{ mt: 0.5 }}>
      <Link component="button" type="button" variant="caption" onClick={handleOpen} underline="hover">
        View Available Variables
      </Link>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { maxWidth: 640, maxHeight: 480 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Available Template Variables
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Use inside <code>{"{{ ... }}"}</code> placeholders in the payload template.
          </Typography>

          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {error && <ApiErrorMessage apiError={error} />}

          {!isLoading && !error && variables.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No variables are defined for this event type.
            </Typography>
          )}

          {!isLoading && !error && variables.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Type</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Description</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variables.map((variable: ApplicationEventTemplateVariable) => (
                  <TableRow key={variable.name}>
                    <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{variable.name}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{variable.type}</TableCell>
                    <TableCell>
                      {variable.description}
                      {variable.example && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", fontFamily: "monospace", mt: 0.5 }}
                        >
                          e.g. {variable.example}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export { TemplateVariablesPopover };
